import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router, RouterState } from '@angular/router';
import { Academic, AuthenticatedIdentity, AuthenticationService, DossierExportPeriod, DossierExportPeriodType, DossierService, HumanResources, SubmissionType } from '../../../domain';
import { Dossier, SelectThemeItems } from '../../../domain/types/dossier';
import { GoogleAnalyticsService } from '../../google-analytics.service';

@Component({
  selector: 'my-pb-data',
  templateUrl: './my-pb-data.component.html',
  styleUrls: ['./my-pb-data.component.scss']
})
export class MyPbDataComponent implements OnInit {
	private activeRoute: RouterState;
	public SubmissionType = SubmissionType;
	dossier: Dossier;
	trim = false;
	allPeriodType = true;
	startDate: Date;
	endDate: Date;
	touchEnabled = false;
	saving = false;
	dossierDownloadButtonVisible: boolean = true;
	meritAndPromotionHR = HumanResources.ANR;
	menuOpen: boolean = false;

	constructor(
	private router: Router,
	private readonly snackbar: MatSnackBar,
	private readonly gaService: GoogleAnalyticsService,
	authSvc: AuthenticationService,
	private svc: DossierService
	){
		// Initialize immediately so it's never undefined on first render
		this.activeRoute = this.router.routerState;

		this.router.events.subscribe(e => {
			if(NavigationEnd.prototype.isPrototypeOf(e)){
				this.activeRoute = this.router.routerState;
			}
		});

		authSvc.currentIdentity.subscribe(i =>
			i.doWithValue((i: AuthenticatedIdentity) => 
				i.user.roles.academic.doWithValue((a: Academic) => { 
					this.dossierDownloadButtonVisible = a.meritAndPromotionHr === HumanResources.ANR;
					this.meritAndPromotionHR = a.meritAndPromotionHr;
				})
			)
		);
	}

	navLinks = [
		{
			url: 'themes', 
			label: 'Themes, Projects, Extension Activities'
		},
		{
			url: 'university-and-public-service', 
			label: 'University and Public Service'
		},
		{
			url: 'professional-competences', 
			label: 'Professional Competence'
		}
	];

	trimmableNavLinks = [this.navLinks[0].url];
	expandableNavLinks = [this.navLinks[0].url];

	urlIsActive(url: string){
		return this.activeRoute.snapshot.url.indexOf(url) > -1;
	}

	ngOnInit() {
		this.svc.getDossier().subscribe(dossier => {
			this.dossier = dossier;
		});
		//Set form fields based on Dossier values
		this.allPeriodType = this.svc.dossierPeriod.type == DossierExportPeriodType.All;
		this.startDate = this.svc.dossierPeriod.startDate; 
		this.endDate = this.svc.dossierPeriod.endDate;
	}

	requirementsMet() {
		if(!!this.dossier) {
			if(this.dossier.ExportStatus.InvalidActivityCount > 0 || this.dossier.ExportStatus.InvlidReasonCount > 0) {
				return false;
			}
		}
		return true;
	}

	getActiveNavLink(){
		var activeNavLink = this.navLinks[0].label;
		this.navLinks.forEach(link => {
			if(link.url == this.activeRoute.snapshot.url.substring(this.activeRoute.snapshot.url.lastIndexOf('/')+1)){
				activeNavLink = link.label;
			}
		});
		return activeNavLink;
	}

	dto(trim: boolean): SelectThemeItems {
		if (!!this.dossier){
			return this.dossier.dto(trim);	
		}
		return new SelectThemeItems(trim, [], [], []);
	}

	canExpand(): boolean {
		let canExpand = false;
		this.expandableNavLinks.forEach(link => {
			if(this.activeRoute.snapshot.url.indexOf(link) >= 0){
				canExpand = true;
			}
		});
		return canExpand;
	}

	trimChanged(){
		this.svc.setTrim(this.trim);
	}

	canTrim(): boolean {
		let canTrim = false;
		this.trimmableNavLinks.forEach(link => {
			if(this.activeRoute.snapshot.url.indexOf(link) >= 0){
				canTrim = true;
			}
		});
		return canTrim;
	}

	typeChanged(): void {
		this.startDate = null;
		this.endDate = null;
		if(this.allPeriodType){ 
			this.svc.dossierPeriod = { type: DossierExportPeriodType.All };
		}
	}

	get exportPeriod(){
		return this.svc.dossierPeriod;
	}

	set exportPeriod(v: DossierExportPeriod){
		this.svc.dossierPeriod = v;
	}

	dateChange(): void {
		this.allPeriodType = false;
		if (this.startDate && this.endDate) {
			this.svc.dossierPeriod = { type: DossierExportPeriodType.Only, startDate: this.startDate, endDate: this.endDate };
		} else if (this.startDate) {
			this.svc.dossierPeriod = { type: DossierExportPeriodType.OnOrAfter, startDate: this.startDate };
		} else if (this.endDate) {
			this.svc.dossierPeriod = { type: DossierExportPeriodType.OnOrBefore, endDate: this.endDate };
		}
	}

	expandAll(expand: boolean): void {
		this.svc.setExpand(expand);
	}

	selectAll(select: boolean): void {
		this.svc.selectAll(select);
		this.snackbar.open('Selection saved', null, {duration: 5000});
	}

	downloadDoc(){
		this.svc.downloadDoc().then(r => r.matchDo(
			_ => {
				this.snackbar.open('Dossier Export is downloading');
				this.gaService.eventEmitter("Dossier Export Downloaded", "Dossier Export", "Download");
			},
			errorMessage => this.snackbar.open(errorMessage,null,{duration:5000})));
	}

	downloadThemesAndProjectsDoc(){
		this.svc.downloadThemesAndProjectsDoc().then(r => r.matchDo(
			_ => {
				this.snackbar.open('Themes and Projects Export is downloading');
				this.gaService.eventEmitter("Themes and Projects Export Downloaded", "Themes and Projects Export", "Download");
			},
			errorMessage => this.snackbar.open(errorMessage,null,{duration:5000})));
	}

	downloadExcel(){
		this.svc.downloadExcel().then(r => r.matchDo(
			_ => {
				this.snackbar.open('Excel Export is downloading');
				this.gaService.eventEmitter("Excel Export Downloaded", "Excel Export", "Download");
			},
			errorMessage => this.snackbar.open(errorMessage,null,{duration:5000})));
	}
}
